import {Link, Text} from '@primer/react'
import {isEnterprise} from '@github-ui/runtime-environment'
import type {CommitSignatureType} from '../signed-commit-types'

export default function ReasonLabel({
  reason,
  isViewer,
  hasSignature,
  verificationStatus,
  signatureType,
  signedByGitHub,
  keyExpired,
  keyRevoked,
}: {
  reason: string
  isViewer: boolean
  hasSignature: boolean
  verificationStatus: string
  signatureType: CommitSignatureType
  signedByGitHub: boolean
  keyExpired: boolean
  keyRevoked: boolean
}) {
  const settings_key_path = `/settings/keys`
  const settings_email_path = `/settings/emails`
  const keyName = signatureType === 'SshSignature' ? 'SSH' : 'GPG'
  if (verificationStatus === 'unverified') {
    if (hasSignature && reason === 'GPGVERIFY_UNAVAILABLE') {
      return <Text>The commit signature verification service is not available</Text>
    } else if (!hasSignature) {
      return (
        <Text>
          This commit is not signed, but one or more authors requires that any commit attributed to them is signed.
        </Text>
      )
    } else {
      switch (reason) {
        case 'INVALID':
          return <Text>The signature in this commit could not be verified. Someone may be trying to trick you.</Text>

        case 'MALFORMED_SIG':
          return <Text>We were unable to parse the signature in this commit.</Text>

        case 'UNKNOWN_KEY':
          if (isViewer) {
            return (
              <Text>
                <Link href={settings_key_path}>Upload your public signing {keyName} key</Link> to verify your signature.
              </Text>
            )
          } else {
            return <Text>This user has not yet uploaded their public signing key.</Text>
          }

        case 'BAD_EMAIL':
          return <Text>The email in this signature doesn’t match the committer email.</Text>

        case 'UNVERIFIED_EMAIL':
          if (isViewer) {
            return (
              <Text>
                <Link href={settings_email_path}>Verify your email address</Link> to verify your signature.
              </Text>
            )
          } else {
            return <Text>The committer email address is not verified.</Text>
          }

        case 'NO_USER':
          return <Text>No user is associated with the committer email.</Text>

        case 'UNKNOWN_SIG_TYPE':
          return (
            <Text>GitHub supports GPG and S/MIME signatures. We don&apos;t know what type of signature this is.</Text>
          )

        case 'GPGVERIFY_ERROR':
          return <Text>We had a problem verifying this signature. Please try again later.</Text>

        case 'NOT_SIGNING_KEY':
          return <Text>The key that signed this doesn&apos;t have usage flags that allow signing.</Text>

        case 'EXPIRED_KEY':
          return <Text>The key that signed this is expired.</Text>

        case 'OCSP_ERROR':
          return <Text>We had a problem checking for revoked certificates. Please try again later.</Text>

        case 'OCSP_REVOKED':
          return <Text>One or more certificates in the chain has been revoked.</Text>

        case 'BAD_CERT':
          return <Text>The signing certificate or its chain could not be verified.</Text>

        case 'GPGVERIFY_UNAVAILABLE':
        case 'UNSIGNED':
          // These cases are handled above.
          return <Text>We were unable to verify this signature.</Text>
      }
    }
  } else if (verificationStatus === 'partially_verified') {
    if (signedByGitHub) {
      if (isEnterprise()) {
        return (
          <Text>
            This commit was created on GitHub Enterprise Server and signed with a{' '}
            <Text sx={{fontWeight: 'bold'}}>verified signature</Text>
          </Text>
        )
      } else {
        return (
          <Text>
            This commit was created on GitHub.com and signed with GitHub&apos;s{' '}
            <Text sx={{fontWeight: 'bold'}}>verified signature</Text>
          </Text>
        )
      }
    } else {
      return (
        <Text>
          This commit was signed with the committer&apos;s <Text sx={{fontWeight: 'bold'}}>verified signature</Text>.
          {keyExpired ? (
            <Text> The key expired {keyRevoked && <Text>and was revoked </Text>}after the commit was signed.</Text>
          ) : keyRevoked ? (
            <Text> The key was revoked after the commit was signed.</Text>
          ) : (
            <></>
          )}
        </Text>
      )
    }
  } else if (verificationStatus === 'verified') {
    switch (reason) {
      case 'VALID':
        if (signedByGitHub) {
          if (isEnterprise()) {
            return (
              <Text>
                This commit was created on GitHub Enterprise Server and signed with a{' '}
                <Text sx={{fontWeight: 'bold'}}>verified signature</Text>
              </Text>
            )
          } else {
            return (
              <Text>
                This commit was created on GitHub.com and signed with GitHub&apos;s{' '}
                <Text sx={{fontWeight: 'bold'}}>verified signature</Text>
              </Text>
            )
          }
        } else {
          return (
            <Text>
              This commit was signed with the committer&apos;s <Text sx={{fontWeight: 'bold'}}>verified signature</Text>
              {keyExpired ? (
                <Text> The key expired {keyRevoked && <Text>and was revoked </Text>}after the commit was signed.</Text>
              ) : keyRevoked ? (
                <Text> The key was revoked after the commit was signed.</Text>
              ) : (
                <></>
              )}
            </Text>
          )
        }
      case 'OCSP_PENDING':
        return (
          <Text>
            This commit was signed with a verified signature, though we&apos;re still working on certificate revocation
            checking.
          </Text>
        )
    }
  }
  return <Text />
}

try{ ReasonLabel.displayName ||= 'ReasonLabel' } catch {}